/**
 * One upload job: the files chosen together, checked, confirmed and sent as one batch (ANON-04 to
 * ANON-07, UPL-01 to UPL-07). It lives outside React and outside any page, so it keeps going while
 * the user works elsewhere in the platform; `jobs.ts` runs the jobs of this tab.
 */
import type { Overrides, Setup } from '../anonymization/engine'
import { api, asApiError, keepingSession, type ApiError } from '../lib/api'
import type { UploadSummary } from '../lib/types'
import { tally, type Tally } from './outcome'
import { ItemStore, LEFT_OUT, checkFiles, followValidation, heldByIdentity, holdContradictions, uploadFiles, type Engines, type Item } from './pipeline'
import type { Sent, SentFiles } from './sent'
import type { Source } from './zip'

export { LEFT_OUT }

export type Phase = 'new' | 'checking' | 'checked' | 'queued' | 'uploading' | 'paused' | 'finishing' | 'done'

/** The phases in which a job works with the token and the engines. */
export const WORKING: ReadonlySet<Phase> = new Set(['checking', 'uploading', 'finishing'])

/** Where the current phase started: when, with how many files done, and the bytes sent since. */
export interface Pace {
  since: number
  doneAtStart: number
  bytes: number
}

export interface JobView {
  readonly id: string
  /** The folders, archives or files chosen, which name the job. */
  readonly roots: readonly string[]
  readonly phase: Phase
  readonly error?: ApiError
  readonly items: readonly Item[]
  readonly tally: Tally
  readonly pace?: Pace
  /** The upload session on the platform, once one started. */
  readonly uploadId: string | null
  /** The key ID of the token the files were checked with. */
  readonly keyId: string | null
}

/** What a job needs to check and send: the engines, the token with its key ID, site and the site's configuration, and the files sent before. */
export interface JobContext {
  engines: Engines & { configure(setup: Setup): Promise<string | null> }
  setup: Setup
  keyId: string
  siteId: string
  sent: SentFiles
}

interface State {
  phase: Phase
  error?: ApiError
  pace?: Pace
  session: { id: string; completed: boolean } | null
  keyId: string | null
}

/** Changes reach the page at most this often, so thousands of file updates cost a few renders. */
const NOTIFY_MS = 100

/** The folders, archives and files chosen: the first part of each file's path. */
const rootsOf = (files: readonly Source[]) => [...new Set(files.map((file) => (file.webkitRelativePath || file.name).split('/')[0]!))]

export class UploadJob {
  readonly id = crypto.randomUUID()
  readonly roots: readonly string[]
  private readonly store = new ItemStore()
  private state: State
  private stopping = false
  private closed = false
  private view: JobView | null = null
  private readonly listeners = new Set<() => void>()
  private timer: ReturnType<typeof setTimeout> | undefined

  constructor(files: readonly Source[]) {
    this.roots = rootsOf(files)
    this.state = { phase: 'new', session: null, keyId: null }
    this.store.replace(files)
    this.store.subscribe(this.changed)
  }

  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => void this.listeners.delete(listener)
  }

  readonly snapshot = (): JobView => {
    if (this.view) return this.view
    const { session, ...state } = this.state
    const items = this.store.snapshot()
    this.view = { id: this.id, roots: this.roots, ...state, items, tally: tally(items), uploadId: session?.id ?? null }
    return this.view
  }

  get phase(): Phase {
    return this.state.phase
  }

  get keyId(): string | null {
    return this.state.keyId
  }

  /** Checks the files not sent yet (ANON-05); those this browser sent before are not read again (UPL-04). */
  async check(context: JobContext): Promise<void> {
    this.stopping = false
    const unsent = this.store.snapshot().filter((item) => !item.serverId)
    this.set({ phase: 'checking', error: undefined, keyId: context.keyId, pace: this.paceFrom(this.store.snapshot().length - unsent.length) })
    try {
      // The engines derive the key ID themselves; a mismatch would give other pseudonyms (TOK-07).
      if ((await context.engines.configure(context.setup)) !== context.keyId) throw new Error('token_no_match')
      const known = await context.sent.present(
        unsent.map((item) => item.file),
        (imageIds) => api<string[]>('/uploads/known', { json: { image_ids: imageIds } }),
      )
      const only = new Set(unsent.map((item) => item.key))
      await keepingSession(() => checkFiles(context.engines, this.store, () => this.stopping, { only, sentBefore: (file) => known.has(file) }))
      this.set({ phase: this.stopping ? 'new' : 'checked', pace: undefined })
    } catch (error) {
      this.fail('new', error)
    }
  }

  /** Checks the chosen files again with corrected or supplied values (ANON-06). */
  async correct(context: JobContext, keys: ReadonlySet<string>, overrides: Overrides): Promise<void> {
    this.store.correct(keys, overrides)
    await this.recheck(context, keys)
  }

  /** Checks the chosen files again, and returns to the phase the job was in. */
  async recheck(context: JobContext, keys: ReadonlySet<string>): Promise<void> {
    const after = this.state.phase
    this.stopping = false
    this.set({ phase: 'checking', error: undefined, pace: undefined })
    try {
      await keepingSession(() => checkFiles(context.engines, this.store, () => this.stopping, { only: keys }))
      this.set({ phase: after })
    } catch (error) {
      this.fail(after, error)
    }
  }

  /**
   * Leaves out files, as the uploader decided before upload (ANON-11, ANON-14): ready ones, or ones
   * the identity check alone holds. The others' identities are compared again without them (TOK-32).
   */
  leaveOut(keys: ReadonlySet<string>, reason = 'burned_in_annotation'): void {
    const chosen = this.store.snapshot().filter((item) => keys.has(item.key) && (item.status === 'waiting' || heldByIdentity(item)))
    this.store.updateMany(new Map(chosen.map((item) => [item.key, { status: 'blocked' as const, reason }])))
    holdContradictions(this.store)
  }

  /** Takes back files the uploader left out while looking through the images; their identities are compared again (TOK-32). */
  keepIn(keys: ReadonlySet<string>): void {
    const chosen = this.store.snapshot().filter((item) => keys.has(item.key) && item.reason === LEFT_OUT)
    this.store.updateMany(new Map(chosen.map((item) => [item.key, { status: 'waiting' as const, reason: undefined }])))
    holdContradictions(this.store)
  }

  /** Waits for its turn to send, after the uploader confirmed both statements (UPL-02). */
  queue(): void {
    this.set({ phase: 'queued', error: undefined })
  }

  unqueue(): void {
    this.set({ phase: this.resumable ? 'paused' : 'checked' })
  }

  /** Sends the ready files: in the paused session, or in a new one (UPL-04). */
  async send(context: JobContext): Promise<void> {
    this.stopping = false
    this.set({ phase: 'uploading', error: undefined, pace: this.paceFrom(this.snapshot().tally.sent) })
    try {
      const session = this.resumable ? this.state.session! : { id: await this.startSession(context.siteId), completed: false }
      this.set({ session })
      const sent = context.sent
      const onSent = (files: Sent[]) => sent.add(files)
      await uploadFiles(context.engines, session.id, this.store, () => this.stopping, { onSent, onBytes: this.addBytes })
      if (this.stopping) return this.set({ phase: 'paused', pace: undefined }) // the files sent are validated meanwhile
      // Every file is sent: the uploader hears the results once all are checked (NTF-01).
      await api(`/uploads/${session.id}/complete`, { method: 'POST' })
      this.set({ phase: 'finishing', session: { ...session, completed: true }, pace: undefined })
      await followValidation(session.id, this.store, () => this.closed, onSent)
      this.set({ phase: 'done' })
    } catch (error) {
      const session = this.state.session
      this.fail(session ? (session.completed ? 'done' : 'paused') : 'checked', error)
    }
  }

  /** Stops after the files in hand: a check returns to the start, an upload pauses. */
  pause(): void {
    this.stopping = true
  }

  /** Checks again from the start, for another token; files sent stay sent. */
  restart(): void {
    this.store.reset()
    this.set({ phase: 'new', error: undefined, session: null, keyId: null, pace: undefined })
  }

  close(): void {
    this.closed = true
    this.stopping = true
    clearTimeout(this.timer)
    this.listeners.clear()
  }

  private get resumable(): boolean {
    return this.state.session !== null && !this.state.session.completed
  }

  /** An upload into the library of the token's site (UPL-03, IMG-04). */
  private async startSession(siteId: string): Promise<string> {
    const json = { confirm_authorized: true, confirm_no_burned_in_text: true, site_id: siteId }
    return (await api<UploadSummary>('/uploads', { json })).id
  }

  private paceFrom(doneAtStart: number): Pace {
    return { since: Date.now(), doneAtStart, bytes: 0 }
  }

  private readonly addBytes = (bytes: number) => {
    const pace = this.state.pace
    if (pace) this.set({ pace: { ...pace, bytes: pace.bytes + bytes } })
  }

  private set(change: Partial<State>): void {
    this.state = { ...this.state, ...change }
    this.changed()
  }

  private fail(phase: Phase, error: unknown): void {
    this.store.failInHand('interrupted')
    this.set({ phase, error: asApiError(error), pace: undefined })
  }

  private readonly changed = () => {
    this.view = null
    if (this.timer !== undefined || this.closed) return
    this.timer = setTimeout(() => {
      this.timer = undefined
      for (const listener of this.listeners) listener()
    }, NOTIFY_MS)
  }
}

/** Files done and files to do in a job's current phase. */
export function progressOf({ phase, tally: counts }: Pick<JobView, 'phase' | 'tally'>): { done: number; of: number } {
  if (phase === 'new' || phase === 'checking') {
    return { done: counts.total - counts.counts.unchecked - counts.counts.progress, of: counts.total }
  }
  return { done: counts.sent, of: counts.planned }
}

/** How fast a working job goes, once there is enough to tell. */
export function paceOf(view: JobView, now = Date.now()): { perSecond: number; bitsPerSecond: number; secondsLeft: number } | undefined {
  if (!view.pace || !WORKING.has(view.phase)) return undefined
  const seconds = (now - view.pace.since) / 1000
  const { done, of } = progressOf(view)
  const moved = done - view.pace.doneAtStart
  if (seconds < 2 || moved < 1) return undefined
  const perSecond = moved / seconds
  return { perSecond, bitsPerSecond: (view.pace.bytes * 8) / seconds, secondsLeft: Math.max(0, (of - done) / perSecond) }
}
